import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  House, Folders, FilePdf, SignOut, Eye, CheckCircle, Clock, ArrowRight,
  Buildings, Fire, Wheelchair, Moon, SpeakerHigh, Download, Pencil, CaretRight
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Architect Layout
const ArchitectLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { path: '/architect', icon: House, label: 'Tableau de bord', exact: true },
    { path: '/architect/dossiers', icon: Folders, label: 'Dossiers' },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex" data-testid="architect-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="font-bold text-xl tracking-tighter font-['Outfit']">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </Link>
          <p className="text-xs text-white/40 mt-1">Espace Architecte</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-white text-[#0A0A0A]'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} weight={isActive(item.path, item.exact) ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="mb-4 px-4">
            <p className="text-sm font-medium text-white">{user?.name}</p>
            <p className="text-xs text-white/40">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2 text-sm text-white/60 hover:text-white w-full"
            data-testid="sidebar-logout-btn"
          >
            <SignOut size={20} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

// Architect Dashboard
const ArchitectDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ assigned: 0, pending: 0, completed: 0 });
  const [availableDossiers, setAvailableDossiers] = useState([]);
  const [myDossiers, setMyDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get all dossiers
        const { data } = await axios.get(`${API}/dossiers`, { withCredentials: true });
        
        // Filter available (unassigned) dossiers
        const available = data.filter(d => !d.architect_id && d.status === 'en_attente');
        // Filter my assigned dossiers
        const mine = data.filter(d => d.architect_id === user?.id);
        
        setAvailableDossiers(available.slice(0, 5));
        setMyDossiers(mine.slice(0, 5));
        setStats({
          assigned: mine.length,
          pending: mine.filter(d => d.status === 'en_cours').length,
          completed: mine.filter(d => d.status === 'termine').length
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleAssign = async (dossierId) => {
    try {
      await axios.put(`${API}/dossiers/${dossierId}/assign`, {}, { withCredentials: true });
      toast.success('Dossier assigné avec succès');
      // Refresh data
      window.location.reload();
    } catch (err) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const poleIcons = {
    terrasse: Buildings,
    securite: Fire,
    accessibilite: Wheelchair,
    licence: Moon,
    sonorisation: SpeakerHigh
  };

  const poleLabels = {
    terrasse: 'Terrasses',
    securite: 'Sécurité Incendie',
    accessibilite: 'Accessibilité PMR',
    licence: 'Licences de Nuit',
    sonorisation: 'Sonorisation'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <p className="text-[#52525B]">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="architect-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Bonjour, {user?.name}</h1>
        <p className="text-[#52525B]">Bienvenue dans votre espace architecte CARRÉ CHR</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB] mb-8">
        <div className="p-6 bg-white border-r border-[#E5E7EB]" data-testid="stat-assigned">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Dossiers assignés</p>
          <p className="text-4xl font-medium text-[#0A0A0A]">{stats.assigned}</p>
        </div>
        <div className="p-6 bg-white border-r border-[#E5E7EB]" data-testid="stat-pending">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">En cours</p>
          <p className="text-4xl font-medium text-[#E53935]">{stats.pending}</p>
        </div>
        <div className="p-6 bg-white" data-testid="stat-completed">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Terminés</p>
          <p className="text-4xl font-medium text-[#16A34A]">{stats.completed}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Available Dossiers */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="p-4 border-b border-[#E5E7EB] bg-[#FEF2F2]">
            <h2 className="font-medium text-[#0A0A0A]">Dossiers disponibles</h2>
            <p className="text-xs text-[#52525B]">Dossiers en attente d'un architecte</p>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {availableDossiers.length === 0 ? (
              <p className="p-4 text-[#52525B] text-sm">Aucun dossier disponible</p>
            ) : (
              availableDossiers.map((dossier) => {
                const Icon = poleIcons[dossier.pole] || Folders;
                return (
                  <div key={dossier.id} className="p-4 flex items-center gap-4" data-testid={`available-dossier-${dossier.id}`}>
                    <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                      <Icon size={20} className="text-[#0A0A0A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A0A0A] truncate">{dossier.title}</p>
                      <p className="text-xs text-[#52525B]">{poleLabels[dossier.pole]} • {dossier.address}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAssign(dossier.id)}
                      className="bg-[#0A0A0A] hover:bg-[#27272A] rounded-none"
                      data-testid={`assign-dossier-${dossier.id}`}
                    >
                      Prendre
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* My Dossiers */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="p-4 border-b border-[#E5E7EB] flex justify-between items-center">
            <div>
              <h2 className="font-medium text-[#0A0A0A]">Mes dossiers</h2>
              <p className="text-xs text-[#52525B]">Dossiers en cours de traitement</p>
            </div>
            <Link to="/architect/dossiers" className="text-sm text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
              Voir tout <CaretRight size={16} />
            </Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {myDossiers.length === 0 ? (
              <p className="p-4 text-[#52525B] text-sm">Aucun dossier assigné</p>
            ) : (
              myDossiers.map((dossier) => {
                const Icon = poleIcons[dossier.pole] || Folders;
                return (
                  <Link
                    key={dossier.id}
                    to={`/architect/dossiers/${dossier.id}`}
                    className="p-4 flex items-center gap-4 hover:bg-[#F4F4F5] transition-colors"
                    data-testid={`my-dossier-${dossier.id}`}
                  >
                    <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                      <Icon size={20} className="text-[#0A0A0A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A0A0A] truncate">{dossier.title}</p>
                      <p className="text-xs text-[#52525B]">{poleLabels[dossier.pole]}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 border ${dossier.status === 'en_cours' ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-green-100 text-green-800 border-green-200'}`}>
                      {dossier.status === 'en_cours' ? 'En cours' : 'Terminé'}
                    </span>
                    <CaretRight size={16} className="text-[#52525B]" />
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Architect Dossiers List
const ArchitectDossiersList = () => {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [availableDossiers, setAvailableDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('mine');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API}/dossiers`, { withCredentials: true });
        setDossiers(data.filter(d => d.architect_id === user?.id));
        setAvailableDossiers(data.filter(d => !d.architect_id && d.status === 'en_attente'));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleAssign = async (dossierId) => {
    try {
      await axios.put(`${API}/dossiers/${dossierId}/assign`, {}, { withCredentials: true });
      toast.success('Dossier assigné avec succès');
      window.location.reload();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const poleLabels = {
    terrasse: 'Terrasses',
    securite: 'Sécurité Incendie',
    accessibilite: 'Accessibilité PMR',
    licence: 'Licences de Nuit',
    sonorisation: 'Sonorisation'
  };

  const statusColors = {
    en_attente: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    en_cours: 'bg-blue-100 text-blue-800 border-blue-200',
    termine: 'bg-green-100 text-green-800 border-green-200'
  };

  const currentList = tab === 'mine' ? dossiers : availableDossiers;

  return (
    <div className="p-8" data-testid="architect-dossiers-list">
      <div className="mb-8">
        <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Dossiers</h1>
        <p className="text-[#52525B]">Gérez les dossiers de conformité</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-[#E5E7EB]">
        <button
          onClick={() => setTab('mine')}
          className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${tab === 'mine' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#52525B]'}`}
          data-testid="tab-mine"
        >
          Mes dossiers ({dossiers.length})
        </button>
        <button
          onClick={() => setTab('available')}
          className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${tab === 'available' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#52525B]'}`}
          data-testid="tab-available"
        >
          Disponibles ({availableDossiers.length})
        </button>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : currentList.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Folders size={48} className="text-[#E5E7EB] mx-auto mb-4" />
          <p className="text-[#52525B]">{tab === 'mine' ? 'Aucun dossier assigné' : 'Aucun dossier disponible'}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Dossier</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Pôle</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Statut</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Date</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {currentList.map((dossier) => (
                <tr key={dossier.id} className="hover:bg-[#F4F4F5]" data-testid={`dossier-row-${dossier.id}`}>
                  <td className="p-4">
                    <p className="font-medium text-[#0A0A0A]">{dossier.title}</p>
                    <p className="text-sm text-[#52525B]">{dossier.address}</p>
                  </td>
                  <td className="p-4 text-sm text-[#52525B]">{poleLabels[dossier.pole]}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 border ${statusColors[dossier.status]}`}>
                      {dossier.status === 'en_attente' ? 'En attente' : dossier.status === 'en_cours' ? 'En cours' : 'Terminé'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-[#52525B]">
                    {format(new Date(dossier.created_at), 'dd/MM/yyyy')}
                  </td>
                  <td className="p-4">
                    {tab === 'available' ? (
                      <Button
                        size="sm"
                        onClick={() => handleAssign(dossier.id)}
                        className="bg-[#0A0A0A] hover:bg-[#27272A] rounded-none"
                        data-testid={`assign-btn-${dossier.id}`}
                      >
                        Prendre
                      </Button>
                    ) : (
                      <Link to={`/architect/dossiers/${dossier.id}`}>
                        <Button variant="ghost" size="sm" className="rounded-none">
                          <Eye size={16} />
                        </Button>
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Architect Dossier Detail
const ArchitectDossierDetail = () => {
  const { id } = require('react-router-dom').useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [measurements, setMeasurements] = useState({});
  const [legalInfo, setLegalInfo] = useState({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const { data } = await axios.get(`${API}/dossiers/${id}`, { withCredentials: true });
        setDossier(data);
        setMeasurements(data.measurements || {});
        setLegalInfo(data.legal_info || {});
        setNotes(data.notes || '');
      } catch (err) {
        toast.error('Dossier non trouvé');
        navigate('/architect/dossiers');
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [id, navigate]);

  const handleSave = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/dossiers/${id}`, {
        measurements,
        legal_info: legalInfo,
        notes
      }, { withCredentials: true });
      toast.success('Dossier mis à jour');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setUpdating(false);
    }
  };

  const handleComplete = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/dossiers/${id}`, { status: 'termine' }, { withCredentials: true });
      toast.success('Dossier marqué comme terminé');
      setDossier({ ...dossier, status: 'termine' });
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setUpdating(false);
    }
  };

  const handleGeneratePDF = async (templateType) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/pdf/generate`, {
        dossier_id: id,
        template_type: templateType
      }, { withCredentials: true, responseType: 'blob' });
      
      // Download PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateType}_${dossier.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF généré avec succès');
    } catch (err) {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const poleLabels = {
    terrasse: 'Terrasses',
    securite: 'Sécurité Incendie',
    accessibilite: 'Accessibilité PMR',
    licence: 'Licences de Nuit',
    sonorisation: 'Sonorisation'
  };

  const poleTemplates = {
    securite: 'fire_safety',
    accessibilite: 'accessibility',
    terrasse: 'terrace'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <p className="text-[#52525B]">Chargement...</p>
      </div>
    );
  }

  if (!dossier) return null;

  return (
    <div className="p-8" data-testid="architect-dossier-detail">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <button onClick={() => navigate('/architect/dossiers')} className="text-sm text-[#52525B] hover:text-[#0A0A0A] mb-4 flex items-center gap-1">
            ← Retour aux dossiers
          </button>
          <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">{dossier.title}</h1>
          <p className="text-[#52525B]">{poleLabels[dossier.pole]} • {dossier.address}</p>
        </div>
        <div className="flex gap-4">
          {dossier.status !== 'termine' && (
            <Button
              onClick={handleComplete}
              disabled={updating}
              className="bg-[#16A34A] hover:bg-[#15803D] rounded-none"
              data-testid="complete-dossier-btn"
            >
              <CheckCircle size={20} className="mr-2" /> Terminer le dossier
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info Card */}
          <div className="bg-white border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-medium text-[#0A0A0A] mb-4">Informations du dossier</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#52525B]">Type d'établissement</p>
                <p className="font-medium">{dossier.facility_type || 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-[#52525B]">Surface</p>
                <p className="font-medium">{dossier.surface_area ? `${dossier.surface_area} m²` : 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-[#52525B]">Capacité d'accueil</p>
                <p className="font-medium">{dossier.occupants ? `${dossier.occupants} personnes` : 'Non renseigné'}</p>
              </div>
              <div>
                <p className="text-[#52525B]">Statut</p>
                <span className={`text-xs px-2 py-1 border ${dossier.status === 'termine' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`}>
                  {dossier.status === 'termine' ? 'Terminé' : 'En cours'}
                </span>
              </div>
            </div>
          </div>

          {/* Measurements */}
          <div className="bg-white border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-medium text-[#0A0A0A] mb-4">Mesures et données techniques</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Longueur (m)</label>
                <Input
                  type="number"
                  value={measurements.length || ''}
                  onChange={(e) => setMeasurements({...measurements, length: e.target.value})}
                  className="rounded-none"
                  data-testid="measurement-length"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Largeur (m)</label>
                <Input
                  type="number"
                  value={measurements.width || ''}
                  onChange={(e) => setMeasurements({...measurements, width: e.target.value})}
                  className="rounded-none"
                  data-testid="measurement-width"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Hauteur (m)</label>
                <Input
                  type="number"
                  value={measurements.height || ''}
                  onChange={(e) => setMeasurements({...measurements, height: e.target.value})}
                  className="rounded-none"
                  data-testid="measurement-height"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Nb sorties secours</label>
                <Input
                  type="number"
                  value={measurements.emergency_exits || ''}
                  onChange={(e) => setMeasurements({...measurements, emergency_exits: e.target.value})}
                  className="rounded-none"
                  data-testid="measurement-exits"
                />
              </div>
            </div>
          </div>

          {/* Legal Info */}
          <div className="bg-white border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-medium text-[#0A0A0A] mb-4">Informations légales</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">N° SIRET</label>
                <Input
                  value={legalInfo.siret || ''}
                  onChange={(e) => setLegalInfo({...legalInfo, siret: e.target.value})}
                  className="rounded-none"
                  data-testid="legal-siret"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">N° Licence</label>
                <Input
                  value={legalInfo.license_number || ''}
                  onChange={(e) => setLegalInfo({...legalInfo, license_number: e.target.value})}
                  className="rounded-none"
                  data-testid="legal-license"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Représentant légal</label>
                <Input
                  value={legalInfo.legal_representative || ''}
                  onChange={(e) => setLegalInfo({...legalInfo, legal_representative: e.target.value})}
                  className="rounded-none"
                  data-testid="legal-rep"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-medium text-[#0A0A0A] mb-4">Notes</h2>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-none"
              rows={4}
              placeholder="Ajoutez vos notes ici..."
              data-testid="dossier-notes"
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={updating}
            className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none"
            data-testid="save-dossier-btn"
          >
            {updating ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
          </Button>
        </div>

        {/* Right: PDF Generation */}
        <div className="space-y-6">
          <div className="bg-white border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-medium text-[#0A0A0A] mb-4">Génération de documents</h2>
            <p className="text-sm text-[#52525B] mb-6">
              Générez automatiquement les documents PDF à partir des informations saisies. L'IA complètera les sections techniques.
            </p>
            
            {poleTemplates[dossier.pole] ? (
              <Button
                onClick={() => handleGeneratePDF(poleTemplates[dossier.pole])}
                disabled={generating}
                className="w-full bg-[#E53935] hover:bg-[#D32F2F] rounded-none mb-3"
                data-testid="generate-pdf-btn"
              >
                <FilePdf size={20} className="mr-2" />
                {generating ? 'Génération...' : 'Générer le PDF'}
              </Button>
            ) : (
              <p className="text-sm text-[#52525B]">
                Template PDF non disponible pour ce pôle.
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-[#F4F4F5] border border-[#E5E7EB] p-6">
            <h3 className="text-sm font-medium text-[#0A0A0A] mb-4">Aide-mémoire</h3>
            <ul className="space-y-2 text-sm text-[#52525B]">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-[#16A34A] mt-0.5" />
                Vérifier les dimensions
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-[#16A34A] mt-0.5" />
                Saisir les infos légales
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-[#16A34A] mt-0.5" />
                Générer le document
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-[#16A34A] mt-0.5" />
                Marquer comme terminé
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ArchitectLayout, ArchitectDashboard, ArchitectDossiersList, ArchitectDossierDetail };
